/**
 * Collapsed summary row for a self-parallel delegation in the list view.
 *
 * A self-parallel spawn fans out N children to the same agent. Rendering
 * each child individually is visually repetitive ("BUG-94 Code chunk 1 ·
 * Coder", "BUG-95 Code chunk 2 · Coder", …), so by default we collapse
 * the whole group into a single row:
 *
 *   └─ 5 self-parallel subtasks · Coder
 *
 * Clicking it expands to show the individual child rows.
 *
 * Spec: docs/specs/2026-04-16-session-state-audit.md §3.5.
 */

import { useCallback } from "react";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";

interface ListViewSelfParallelSummaryProps {
  /** Number of children collapsed into this summary. */
  count: number;
  /** Shared agent name (all children assigned to the same agent). */
  agentName: string | null;
  /** Shared agent color (for the small dot), falls back to gray. */
  agentColor?: string | null;
  /** Indentation level — multiplied by 24px per depth. */
  indent: number;
  /** Click to expand the group and render individual children. */
  onExpand: () => void;
  className?: string;
}

export function ListViewSelfParallelSummary({
  count,
  agentName,
  indent,
  onExpand,
  className,
}: ListViewSelfParallelSummaryProps) {
  const t = useT(framesTaskboardMessages);
  const handleClick = useCallback(() => {
    onExpand();
  }, [onExpand]);

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-control px-4 py-1.5 transition-colors",
        "cursor-pointer",
        className,
      )}
      style={{ paddingLeft: 16 + indent * 24 }}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onExpand();
        }
      }}
      data-testid="listview-self-parallel-summary"
    >
      <span className="text-appkit-small text-muted italic">
        {t("selfParallelSubtasks", { count })}
      </span>
      {agentName && (
        <span className="inline-flex items-center gap-1">
          <span
            aria-hidden="true"
            className="h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: "var(--color-muted)" }}
          />
          <span className="text-appkit-small">{agentName}</span>
        </span>
      )}
    </div>
  );
}
